import React, { useState } from "react";

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  examples?: string[];
  hint?: string;
}

/**
 * A monospace regex pattern input with:
 * - Live match tester (type a test value, see ✓ or ✗ instantly)
 * - Inline examples
 * - Error feedback for invalid patterns
 */
export default function RegexPatternInput({
  value,
  onChange,
  placeholder,
  examples = [],
  hint
}: Props) {
  const [testValue, setTestValue] = useState("");
  const [showTest, setShowTest] = useState(false);

  const patternError = (() => {
    if (!value) return null;
    try {
      new RegExp(value, "i");
      return null;
    } catch {
      return "Invalid regex pattern";
    }
  })();

  const testResult = (() => {
    if (!value || !testValue || patternError) return null;
    try {
      return new RegExp(value, "i").test(testValue);
    } catch {
      return null;
    }
  })();

  return (
    <div className="flex flex-col gap-1.5">
      {/* Pattern input */}
      <div className="relative">
        <input
          type="text"
          className={`w-full border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 ${
            patternError
              ? "border-red-400 focus:ring-red-300 bg-red-50"
              : "border-slate-300 focus:ring-blue-300 bg-white"
          }`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "e.g. ^https://.*\\.example\\.com"}
          spellCheck={false}
        />
        {value && !patternError && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-500 text-xs select-none">
            ✓ valid
          </span>
        )}
        {patternError && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 text-xs select-none">
            ✗ invalid
          </span>
        )}
      </div>

      {/* Error message */}
      {patternError && (
        <p className="text-xs text-red-500">{patternError}</p>
      )}

      {/* Info strip */}
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 flex flex-col gap-1.5">
        {hint && (
          <p className="text-xs text-slate-500">{hint}</p>
        )}

        {examples.length > 0 && (
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
              Examples
            </span>
            {examples.map((ex, i) => (
              <button
                key={i}
                type="button"
                className="text-left text-xs font-mono text-blue-600 hover:text-blue-800 hover:underline w-fit"
                onClick={() => onChange(ex)}
                title="Click to use this pattern"
              >
                {ex}
              </button>
            ))}
          </div>
        )}

        {/* Live tester toggle */}
        <button
          type="button"
          className="text-[10px] font-semibold text-slate-400 hover:text-blue-600 uppercase tracking-wider w-fit flex items-center gap-1"
          onClick={() => setShowTest((v) => !v)}
        >
          <span>{showTest ? "▾" : "▸"}</span>
          Test pattern
        </button>

        {showTest && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
              placeholder="Type a test value..."
              value={testValue}
              onChange={(e) => setTestValue(e.target.value)}
            />
            {testResult === true && (
              <span className="text-emerald-600 text-sm font-semibold shrink-0">✓ match</span>
            )}
            {testResult === false && (
              <span className="text-red-500 text-sm font-semibold shrink-0">✗ no match</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
